import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Card,
  CardContent,
  Grid,
  Switch,
  FormControlLabel,
  Stepper,
  Step,
  StepLabel,
  Chip,
  Divider,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { withStyles } from '@material-ui/core/styles';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import SaveIcon from '@material-ui/icons/Save';
import CancelIcon from '@material-ui/icons/Cancel';
import { useHistory } from 'react-router-dom';

const styles = theme => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  formField: {
    marginBottom: theme.spacing(2),
  },
  stepCard: {
    marginBottom: theme.spacing(2),
    border: '2px solid #e0e0e0',
  },
  stepHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  stepActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  addStepButton: {
    marginTop: theme.spacing(2),
  },
  actionButtons: {
    display: 'flex',
    gap: theme.spacing(2),
    justifyContent: 'flex-end',
    marginTop: theme.spacing(3),
  },
  timeline: {
    padding: theme.spacing(2),
    backgroundColor: '#f5f5f5',
    borderRadius: 4,
    marginTop: theme.spacing(2),
  },
  delayInputs: {
    display: 'flex',
    gap: theme.spacing(2),
  },
});

const SequenceBuilder = ({
  classes,
  sequence,
  templates,
  onSave,
  onCancel,
}) => {
  const history = useHistory();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [sequenceTagList, setSequenceTagList] = useState([]);
  const [active, setActive] = useState(true);
  const [steps, setSteps] = useState([]);

  useEffect(() => {
    if (sequence) {
      setName(sequence.name || '');
      setDescription(sequence.description || '');
      setCategory(sequence.category || '');
      setSequenceTagList(sequence.sequence_tag_list || []);
      setActive(sequence.active !== undefined ? sequence.active : true);
      setSteps(
        sequence.emailSequenceSteps?.map((step, index) => ({
          id: step.id,
          position: step.position || index + 1,
          templateId: step.emailTemplate?.id || '',
          delayDays: step.delayDays || 0,
          delayHours: step.delayHours || 0,
          delayMinutes: step.delayMinutes || 0,
        })) || []
      );
    }
  }, [sequence]);

  const addStep = () => {
    setSteps([
      ...steps,
      {
        id: `new-${Date.now()}`,
        position: steps.length + 1,
        templateId: '',
        delayDays: 0,
        delayHours: 0,
        delayMinutes: 0,
      },
    ]);
  };

  const removeStep = (index) => {
    const newSteps = steps.filter((_, i) => i !== index);
    // Reorder positions
    newSteps.forEach((step, i) => {
      step.position = i + 1;
    });
    setSteps(newSteps);
  };

  const moveStep = (index, direction) => {
    const newSteps = [...steps];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (targetIndex >= 0 && targetIndex < newSteps.length) {
      [newSteps[index], newSteps[targetIndex]] = [newSteps[targetIndex], newSteps[index]];
      // Reorder positions
      newSteps.forEach((step, i) => {
        step.position = i + 1;
      });
      setSteps(newSteps);
    }
  };

  const updateStep = (index, field, value) => {
    const newSteps = [...steps];
    newSteps[index][field] = value;
    setSteps(newSteps);
  };

  const calculateTotalDurationMinutes = () => {
    return steps.reduce((total, step) => {
      return total + (step.delayDays * 24 * 60) + (step.delayHours * 60) + (step.delayMinutes || 0);
    }, 0);
  };

  const formatDuration = (totalMinutes) => {
    const days = Math.floor(totalMinutes / (24 * 60));
    const hours = Math.floor((totalMinutes % (24 * 60)) / 60);
    const minutes = totalMinutes % 60;
    
    const parts = [];
    if (days > 0) parts.push(`${days}d`);
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    
    return parts.length > 0 ? parts.join(' ') : '0m';
  };

  const getStepDelayMinutes = (step) => {
    return (step.delayDays * 24 * 60) + (step.delayHours * 60) + (step.delayMinutes || 0);
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a sequence name');
      return;
    }

    if (steps.length === 0) {
      alert('Please add at least one step to the sequence');
      return;
    }

    const invalidSteps = steps.filter(step => !step.templateId);
    if (invalidSteps.length > 0) {
      alert('Please select a template for all steps');
      return;
    }

    const sequenceData = {
      name: name.trim(),
      description: description.trim(),
      category: category.trim() || null,
      sequenceTagList,
      active,
      steps: steps.map(step => ({
        templateId: step.templateId,
        delayDays: parseInt(step.delayDays) || 0,
        delayHours: parseInt(step.delayHours) || 0,
        delayMinutes: parseInt(step.delayMinutes) || 0,
        position: step.position,
      })),
    };

    if (sequence?.id) {
      sequenceData.id = sequence.id;
    }

    if (onSave) {
      onSave(sequenceData);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      history.push('/admin/email_sequences');
    }
  };

  const getTemplateName = (templateId) => {
    const template = templates.find(t => t.id === templateId);
    return template ? template.name : 'Unknown Template';
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h4">
          {sequence?.id ? 'Edit Sequence' : 'Create New Sequence'}
        </Typography>
      </div>

      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Sequence Details
        </Typography>

        <TextField
          label="Sequence Name"
          variant="outlined"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={classes.formField}
        />

        <TextField
          label="Description"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className={classes.formField}
          helperText="Describe the purpose and goal of this sequence"
        />

        <TextField
          label="Category"
          variant="outlined"
          fullWidth
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className={classes.formField}
          helperText="Assign a category to help organize sequences (e.g. Onboarding, Retention)"
        />

        <Autocomplete
          multiple
          freeSolo
          autoSelect
          options={[]}
          value={sequenceTagList}
          onChange={(_e, newValue) => setSequenceTagList(newValue)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                key={option}
                label={option}
                size="small"
                {...getTagProps({ index })}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label="Tags"
              placeholder="Type a tag and press Enter"
              helperText="Add tags to help find this sequence"
              className={classes.formField}
            />
          )}
        />

        <FormControlLabel
          control={
            <Switch
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              color="primary"
            />
          }
          label="Active (users can be enrolled in this sequence)"
        />
      </Paper>

      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          Sequence Steps
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Build your email sequence by adding steps. Each step sends an email after a specified delay.
        </Typography>

        {steps.length > 0 && (
          <Box className={classes.timeline} mb={2}>
            <Stepper activeStep={-1} alternativeLabel>
              {steps.map((step, index) => (
                <Step key={step.id} completed={false}>
                  <StepLabel>
                    <Typography variant="caption">
                      {index === 0 ? 'Immediate' : formatDuration(getStepDelayMinutes(step))}
                    </Typography>
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
            <Typography variant="body2" align="center" style={{ marginTop: 8 }}>
              Total Duration: {formatDuration(calculateTotalDurationMinutes())}
            </Typography>
          </Box>
        )}

        {steps.map((step, index) => (
          <Card key={step.id} className={classes.stepCard}>
            <CardContent>
              <div className={classes.stepHeader}>
                <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                  Step {step.position}
                  {index === 0 && (
                    <Chip
                      label="Immediate"
                      size="small"
                      color="primary"
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </Typography>
                <div className={classes.stepActions}>
                  <IconButton
                    size="small"
                    onClick={() => moveStep(index, 'up')}
                    disabled={index === 0}
                  >
                    <ArrowUpwardIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => moveStep(index, 'down')}
                    disabled={index === steps.length - 1}
                  >
                    <ArrowDownwardIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => removeStep(index)}
                    color="secondary"
                  >
                    <DeleteIcon />
                  </IconButton>
                </div>
              </div>

              <Divider style={{ marginBottom: 16 }} />

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl variant="outlined" fullWidth required>
                    <InputLabel>Email Template</InputLabel>
                    <Select
                      value={step.templateId}
                      onChange={(e) => updateStep(index, 'templateId', e.target.value)}
                      label="Email Template"
                    >
                      <MenuItem value="">
                        <em>Select a template</em>
                      </MenuItem>
                      {templates.map(template => (
                        <MenuItem key={template.id} value={template.id}>
                          {template.name}
                          <Chip
                            label={template.category}
                            size="small"
                            style={{ marginLeft: 8 }}
                          />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {index > 0 && (
                  <>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        Delay before sending (from previous step)
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Days"
                        variant="outlined"
                        type="number"
                        fullWidth
                        value={step.delayDays}
                        onChange={(e) => updateStep(index, 'delayDays', Math.max(0, parseInt(e.target.value) || 0))}
                        inputProps={{ min: 0 }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Hours"
                        variant="outlined"
                        type="number"
                        fullWidth
                        value={step.delayHours}
                        onChange={(e) => updateStep(index, 'delayHours', Math.max(0, parseInt(e.target.value) || 0))}
                        inputProps={{ min: 0, max: 23 }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        label="Minutes"
                        variant="outlined"
                        type="number"
                        fullWidth
                        value={step.delayMinutes || 0}
                        onChange={(e) => updateStep(index, 'delayMinutes', Math.max(0, parseInt(e.target.value) || 0))}
                        inputProps={{ min: 0, max: 59 }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </CardContent>
          </Card>
        ))}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addStep}
          className={classes.addStepButton}
          fullWidth
        >
          Add Step
        </Button>
      </Paper>

      <div className={classes.actionButtons}>
        <Button
          variant="outlined"
          startIcon={<CancelIcon />}
          onClick={handleCancel}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SaveIcon />}
          onClick={handleSave}
        >
          {sequence?.id ? 'Update Sequence' : 'Create Sequence'}
        </Button>
      </div>
    </div>
  );
};

SequenceBuilder.propTypes = {
  classes: PropTypes.object.isRequired,
  sequence: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    active: PropTypes.bool,
    emailSequenceSteps: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        position: PropTypes.number,
        delayDays: PropTypes.number,
        delayHours: PropTypes.number,
        delayMinutes: PropTypes.number,
        emailTemplate: PropTypes.shape({
          id: PropTypes.string,
          name: PropTypes.string,
        }),
      })
    ),
  }),
  templates: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      category: PropTypes.string,
    })
  ).isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
};

SequenceBuilder.defaultProps = {
  templates: [],
};

export default withStyles(styles)(SequenceBuilder);

